import { useEffect, useMemo, useState } from 'react';
import { DevicesGroupSchedulerMode, usePointsGroupCreateMutation } from '@shared/api/graphql/generated';
import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import { IVideoStream } from 'application/pages/PointsGroupsPage/types';
import { useAppSelector, useAppDispatch } from 'application/providers/StoreProvider';
import { selectCompanyIds } from 'application/store';
import { TEXTS } from '../../texts';
import { selectDevices, selectMasterVideoStream, selectTitle } from '../../slices/createPointsGroup/selectors';
import { setMasterVideoStream, setStep } from '../../slices/createPointsGroup/slice';
import { CREATE_POINTS_GROUP_STEPS } from '../../slices/createPointsGroup/consts';

interface IUseSelectMasterVideoStreamViewModel {
  close: () => void;
}

const BINDED_TO_OTHER_GROUPS = /binded to other groups/;
const PLAY_DURATION_ALIGNMENT = /play_duration_alignment/;

export const useSelectMasterVideoStreamViewModel = ({ close }: IUseSelectMasterVideoStreamViewModel) => {
  const [masterVideoStreamId, setMasterVideoStreamId] = useState<string | number>('');
  const companyIds = useAppSelector(selectCompanyIds);
  const dispatch = useAppDispatch();

  const devices = useAppSelector(selectDevices);
  const title = useAppSelector(selectTitle);

  const masterVideoStream = useAppSelector(selectMasterVideoStream);

  const [mutate, mutationResult] = usePointsGroupCreateMutation();

  const createPointsGroup = async () => {
    const deviceIds = devices?.map((it) => it.id).filter(Boolean) as string[];
    if (masterVideoStreamId && deviceIds.length > 0) {
      const result = await mutate({
        variables: {
          input: {
            masterVideoStreamId: String(masterVideoStreamId),
            companyId: companyIds[0],
            deviceIds,
            title,
            schedulerMode: DevicesGroupSchedulerMode.PlayDurationAlignment,
          },
        },
        refetchQueries: ['pointsGroups'],
      });

      if (result.data?.smartlkDevicesGroupCreate?.ok) {
        toast.info(TEXTS.CREATE_GROUP_SUCCESS);
      } else {
        const message = result?.data?.smartlkDevicesGroupCreate?.errors?.[0]?.messages?.[0];
        if (message && (BINDED_TO_OTHER_GROUPS.test(message) || PLAY_DURATION_ALIGNMENT.test(message))) {
          toast.error(TEXTS.BINDED_TO_OTHER_GROUPS_ERROR);

          return;
        }

        toast.error(result?.data?.smartlkDevicesGroupCreate?.errors?.[0]?.messages?.[0] || DEFAULT_ERROR_MESSAGE);
      }
    }
    close();
  };

  useEffect(() => {
    const currentMasterVideoStreamId = masterVideoStream?.id;

    if (currentMasterVideoStreamId) {
      setMasterVideoStreamId(currentMasterVideoStreamId);
    }
  }, [masterVideoStream?.id]);

  const isButtonDisabled = !masterVideoStreamId;

  const videoStreams = useMemo(() => {
    const idsSet = new Set<string | number>();
    return devices?.reduce((acc, it) => {
      const videoStream = it.point?.videoStream;
      if (videoStream && !idsSet.has(videoStream.id)) {
        idsSet.add(videoStream.id);
        acc.push(videoStream);
      }

      return acc;
    }, [] as IVideoStream[]);
  }, [devices]);

  const onBack = () => {
    const newCurrentMasterVideoStream = videoStreams?.find((it) => String(it.id) === String(masterVideoStreamId));
    if (newCurrentMasterVideoStream) {
      dispatch(setMasterVideoStream(newCurrentMasterVideoStream));
    }
    dispatch(setStep(CREATE_POINTS_GROUP_STEPS.DEVICES));
  };

  return {
    isButtonDisabled,
    createPointsGroup,
    isButtonLoading: mutationResult.loading,
    videoStreams,
    setMasterVideoStreamId,
    masterVideoStreamId,
    onBack,
  };
};
